import React, { useState } from 'react';
import {
  Card, CardBody, Collapse, Badge, Nav, NavItem, NavLink, TabContent, TabPane, Button,
} from 'reactstrap';
import {
  ChevronDown, ChevronRight, ExternalLink, Copy, Download, AlertTriangle,
} from 'react-feather';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  success: 'success', error: 'danger', config_error: 'warning', skipped: 'secondary',
};

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied to clipboard'),
    () => toast.error('Copy failed'),
  );
}

function download(text, filename) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Inject a <base href> so the rendered preview resolves the page's relative
// CSS/image URLs against the original site (otherwise it looks unstyled/broken).
function withBase(html, url) {
  if (!html || !url) return html;
  const base = `<base href="${url}">`;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${base}`);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html([^>]*)>/i, `<html$1><head>${base}</head>`);
  return base + html;
}

// One scraped URL: a rendered-HTML preview tab + a raw-JSON tab.
function ScrapedUrl({ hit, rowIndex, urlIndex }) {
  const html = hit.html || '';
  const srcDoc = withBase(html, hit.url);
  const rawJson = JSON.stringify(hit.raw ?? {}, null, 2);
  const [activeTab, setActiveTab] = useState('html');

  return (
    <Card className="mb-3 border">
      <CardBody className="py-2">
        <div className="d-flex justify-content-between align-items-start">
          <div className="me-2" style={{ minWidth: 0 }}>
            <div className="fw-bold text-truncate">{hit.serp_title || '(no title)'}</div>
            <a href={hit.url} target="_blank" rel="noreferrer" className="small text-break">
              {hit.url} <ExternalLink size={12} />
            </a>
            {hit.description && <div className="text-muted small mt-1">{hit.description}</div>}
          </div>
          <div className="text-nowrap text-end">
            {hit.position != null && <Badge color="light" className="text-dark me-1">#{hit.position}</Badge>}
            {hit.status_code != null && <Badge color="light" className="text-dark">HTTP {hit.status_code}</Badge>}
          </div>
        </div>

        {hit.error && (
          <div className="text-danger small mt-2">
            <AlertTriangle size={13} className="me-1" />{hit.error}
          </div>
        )}

        {(html || hit.raw) && (
          <div className="mt-2">
            <Nav tabs className="small">
              <NavItem>
                <NavLink href="#" active={activeTab === 'html'} onClick={(e) => { e.preventDefault(); setActiveTab('html'); }}>
                  HTML
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink href="#" active={activeTab === 'json'} onClick={(e) => { e.preventDefault(); setActiveTab('json'); }}>
                  Raw JSON
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={activeTab} className="border border-top-0 rounded-bottom p-2">
              <TabPane tabId="html">
                <div className="d-flex justify-content-end gap-2 mb-1">
                  <Button color="link" size="sm" className="p-0" onClick={() => copyToClipboard(html)}>
                    <Copy size={14} className="me-1" />Copy HTML
                  </Button>
                  <Button color="link" size="sm" className="p-0" onClick={() => download(html, `row${rowIndex + 1}_url${urlIndex + 1}.html`)}>
                    <Download size={14} className="me-1" />Download
                  </Button>
                </div>
                {html ? (
                  <iframe
                    title={`scraped-${rowIndex}-${urlIndex}`}
                    sandbox=""
                    srcDoc={srcDoc}
                    style={{ width: '100%', height: 380, border: '1px solid #eee', background: '#fff' }}
                  />
                ) : (
                  <div className="text-muted fst-italic small">No HTML returned.</div>
                )}
              </TabPane>
              <TabPane tabId="json">
                <div className="d-flex justify-content-end gap-2 mb-1">
                  <Button color="link" size="sm" className="p-0" onClick={() => copyToClipboard(rawJson)}>
                    <Copy size={14} className="me-1" />Copy JSON
                  </Button>
                  <Button color="link" size="sm" className="p-0" onClick={() => download(rawJson, `row${rowIndex + 1}_url${urlIndex + 1}.json`)}>
                    <Download size={14} className="me-1" />Download
                  </Button>
                </div>
                <pre className="bg-light p-2 rounded mb-0" style={{ maxHeight: 380, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {rawJson}
                </pre>
              </TabPane>
            </TabContent>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default function ResultRow({ row, index }) {
  const [open, setOpen] = useState(true);
  const color = STATUS_COLORS[row.status] || 'secondary';

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
            {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span className="fw-bold ms-2 me-2">Row {index + 1}</span>
            <code className="text-primary text-truncate">{row.query || '(empty query)'}</code>
          </div>
          <div className="text-nowrap ms-2">
            <Badge color={color} className="me-2 text-uppercase">{row.status}</Badge>
            <Badge color="light" className="text-dark me-2">{row.url_count} URL(s)</Badge>
            {row.credits_used != null && <Badge color="light" className="text-dark">{row.credits_used} credits</Badge>}
          </div>
        </div>

        <Collapse isOpen={open} className="mt-3">
          {row.error && (
            <div className="text-danger small mb-2"><AlertTriangle size={14} className="me-1" />{row.error}</div>
          )}
          <div className="text-muted small mb-2">Target: <b>{row.target}</b> · Limit: <b>{row.limit}</b></div>
          {(row.urls || []).length === 0
            ? <div className="text-muted fst-italic">No URLs scraped.</div>
            : row.urls.map((hit, i) => <ScrapedUrl hit={hit} rowIndex={index} urlIndex={i} key={i} />)}
        </Collapse>
      </CardBody>
    </Card>
  );
}
